import { Phone, ArrowRight, Check, X, Shield, Stethoscope, ClipboardList, TestTube, Pill, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';

const WHATSAPP_LINK = "https://wa.me/5541988451399?text=Olá,%20gostaria%20de%20agendar%20uma%20avaliação%20médica%20para%20emagrecimento%20clínico.";

export const EmagrecimentoPage = () => {
  return (
    <main data-testid="emagrecimento-page" className="pt-24">
      {/* Hero */}
      <section className="section-padding bg-[#FAF9F7]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-[0.2em] text-[#B8944D] mb-6">
              Dr. Eduardo Hadas — CRM-PR 40929
            </p>
            
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Emagrecimento não é força de vontade.<br />
              <span className="text-[#B8944D]">É um processo médico.</span>
            </h1>
            
            <p className="text-lg text-[#666666] leading-relaxed mb-6 max-w-2xl">
              Tratamento médico individualizado para emagrecimento sustentável, 
              com critério científico, segurança e acompanhamento contínuo.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="emagrecimento-hero-cta"
                className="btn-primary flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Agendar avaliação médica
              </a>
              <Link
                to="/como-funciona"
                className="btn-secondary flex items-center justify-center gap-2"
              >
                Entenda como funciona
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Por que a maioria das pessoas falha */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Por que a maioria das pessoas falha ao tentar emagrecer?
            </h2>
            
            <div className="space-y-6 text-lg text-[#666666] leading-relaxed">
              <p>
                A maioria das pessoas não falha por falta de esforço.
              </p>
              
              <p>
                Ela falha porque tenta resolver um problema médico complexo com soluções genéricas.
              </p>
              
              <p>
                Dietas prontas, treinos aleatórios, remédios sem acompanhamento e protocolos copiados 
                da internet ignoram algo essencial: <span className="text-[#2A2A2A] font-medium">cada organismo funciona de forma diferente</span>.
              </p>
              
              <p>
                Sem avaliação adequada, sem exames e sem acompanhamento contínuo, o emagrecimento 
                vira um ciclo de tentativa, frustração e efeito rebote.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* O que é emagrecimento clínico */}
      <section className="section-padding bg-[#E5E2DD]/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              O que é emagrecimento clínico?
            </h2>
            
            <p className="text-lg text-[#666666] leading-relaxed mb-8">
              Emagrecimento clínico é um tratamento médico conduzido com base em:
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { icon: ClipboardList, text: 'Avaliação individual' },
                { icon: TestTube, text: 'Exames laboratoriais' },
                { icon: Stethoscope, text: 'Critérios científicos' },
                { icon: Activity, text: 'Acompanhamento contínuo' },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4 bg-white p-4 rounded-md">
                  <item.icon className="w-6 h-6 text-[#B8944D]" />
                  <span className="text-[#2A2A2A]">{item.text}</span>
                </div>
              ))}
            </div>
            
            <p className="text-lg text-[#666666] leading-relaxed">
              Aqui, o objetivo não é apenas perder peso.
            </p>
            
            <p className="text-lg text-[#2A2A2A] font-medium mt-4">
              É recuperar saúde metabólica, equilíbrio hormonal e um peso sustentável ao longo da vida.
            </p>
          </div>
        </div>
      </section>

      {/* Meu modelo de emagrecimento */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Meu modelo de emagrecimento
            </h2>
            
            <p className="text-lg text-[#666666] leading-relaxed mb-8">
              Meu modelo de tratamento não se baseia em dietas prontas, protocolos genéricos ou soluções rápidas.
              Ele se baseia em três pilares:
            </p>
            
            <div className="space-y-6">
              {[
                {
                  number: '01',
                  title: 'Individualização total',
                  description: 'Cada paciente recebe um plano construído especificamente para sua realidade metabólica, hormonal, clínica e emocional.'
                },
                {
                  number: '02',
                  title: 'Critério científico',
                  description: 'Todas as decisões são guiadas por exames, dados clínicos e resposta individual ao tratamento.'
                },
                {
                  number: '03',
                  title: 'Acompanhamento contínuo',
                  description: 'O tratamento não termina na consulta. Ele exige ajustes, reavaliações e suporte ao longo do caminho.'
                },
              ].map((item, index) => (
                <div key={index} className="flex gap-6 items-start">
                  <span 
                    className="text-4xl text-[#B8944D]/30 font-light flex-shrink-0"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {item.number}
                  </span>
                  <div>
                    <h3 
                      className="text-xl text-[#2A2A2A] mb-2"
                      style={{ fontFamily: 'var(--font-heading)' }}
                    >
                      {item.title}
                    </h3>
                    <p className="text-[#666666] leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Como funciona o tratamento */}
      <section className="section-padding bg-[#FAF9F7]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 
              className="section-title mb-12 text-center"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Como funciona o tratamento de emagrecimento clínico?
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  step: '1',
                  title: 'Consulta e anamnese aprofundada',
                  description: 'Entendemos histórico, sintomas, tentativas anteriores, rotina, queixas físicas e emocionais.'
                },
                {
                  step: '2',
                  title: 'Avaliação laboratorial',
                  description: 'Solicitamos exames para entender metabolismo, hormônios, inflamação e deficiências nutricionais.'
                },
                {
                  step: '3',
                  title: 'Estratégia terapêutica individual',
                  description: 'Definimos se há necessidade de ajustes alimentares, medicações, suporte hormonal ou terapias complementares.'
                },
                {
                  step: '4',
                  title: 'Início do tratamento',
                  description: 'Você recebe orientações claras e um plano totalmente personalizado.'
                },
                {
                  step: '5',
                  title: 'Acompanhamento contínuo',
                  description: 'Reavaliações periódicas, ajustes de dose e prevenção de efeitos colaterais.'
                },
              ].map((item, index) => (
                <div key={index} className="card-premium">
                  <div className="step-number mb-4">{item.step}</div>
                  <h3 
                    className="text-lg text-[#2A2A2A] mb-2"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#666666] leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Medicações e suporte hormonal */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-full bg-[#B8944D]/10 flex items-center justify-center flex-shrink-0">
                <Pill className="w-7 h-7 text-[#B8944D]" />
              </div>
              <div>
                <h2 
                  className="section-title mb-6"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Medicações e suporte hormonal
                </h2>
                
                <div className="space-y-6 text-lg text-[#666666] leading-relaxed">
                  <p>
                    Quando indicadas, medicações para emagrecimento e suporte hormonal são utilizadas 
                    com critério e responsabilidade.
                  </p>
                  
                  <p className="text-[#2A2A2A] font-medium">
                    Nunca como atalho. Nunca como solução isolada.
                  </p>
                  
                  <p>
                    Elas fazem parte de um plano médico maior, sempre com acompanhamento e reavaliações periódicas.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Para quem é / Para quem não é */}
      <section className="section-padding bg-[#E5E2DD]/30">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 
              className="section-title"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Este tratamento é para você?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Para quem é */}
            <div className="card-premium bg-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#B8944D]/10 flex items-center justify-center">
                  <Check className="w-5 h-5 text-[#B8944D]" />
                </div>
                <h3 
                  className="text-xl text-[#2A2A2A]"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Para quem é
                </h3>
              </div>
              
              <ul className="space-y-3">
                {[
                  'Para quem já tentou de tudo',
                  'Para quem sofre com efeito rebote',
                  'Para quem suspeita de desequilíbrio hormonal',
                  'Para quem quer emagrecer com segurança',
                  'Para quem aceita acompanhamento contínuo',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#B8944D] flex-shrink-0 mt-0.5" />
                    <span className="text-[#666666]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Para quem não é */}
            <div className="card-premium bg-[#FAF9F7]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#2A2A2A]/10 flex items-center justify-center">
                  <X className="w-5 h-5 text-[#2A2A2A]" />
                </div>
                <h3 
                  className="text-xl text-[#2A2A2A]"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Para quem não é
                </h3>
              </div>
              
              <ul className="space-y-3">
                {[
                  'Para quem quer resultado rápido',
                  'Para quem quer pílula mágica',
                  'Para quem quer dieta da moda',
                  'Para quem não quer fazer exames',
                  'Para quem não quer reavaliações',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-[#888888] flex-shrink-0 mt-0.5" />
                    <span className="text-[#888888]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              O diferencial não é a dieta.
            </h2>
            <p className="text-xl text-[#B8944D] mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              É o acompanhamento.
            </p>
            
            <ul className="grid sm:grid-cols-2 gap-4">
              {[
                'Tratamento médico real',
                'Sem protocolos prontos',
                'Sem atalhos',
                'Sem excessos',
                'Doses mínimas eficazes',
                'Exames e reavaliações',
                'Comunicação direta com o médico',
                'Foco em resultado sustentável',
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#B8944D]" />
                  <span className="text-[#2A2A2A]">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Segurança e ética */}
      <section className="section-padding bg-[#B68A5A]/10">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-8">
              <Shield className="w-8 h-8 text-[#B8944D]" />
            </div>
            
            <h2 
              className="section-title mb-6"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Segurança e ética médica.
            </h2>
            
            <div className="space-y-4 text-lg text-[#666666] leading-relaxed">
              <p className="text-[#2A2A2A] font-medium">
                Meu compromisso nunca é com velocidade.
              </p>
              
              <p>
                É com segurança, saúde e resultado sustentável.
              </p>
              
              <p>
                Nenhum tratamento é iniciado sem avaliação adequada.
              </p>
              
              <p>
                Nenhuma medicação é mantida sem acompanhamento.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-[#2A2A2A] text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 
              className="text-3xl md:text-4xl text-white mb-6 leading-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Se você quer emagrecer sem brincar com seu corpo,{' '}
              <span className="text-[#B8944D]">vamos conversar</span>.
            </h2>
            
            <p className="text-lg text-white/70 mb-10">
              Agende uma avaliação médica e entenda se o meu acompanhamento faz sentido para o seu caso.
            </p>
            
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="emagrecimento-cta-button"
              className="btn-primary inline-flex items-center gap-2 text-lg"
            >
              <Phone className="w-5 h-5" />
              Agendar avaliação médica
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
